import React, { useEffect, useRef, useMemo, useState } from 'react';
import { Button } from '@vibe/core';
import { useUsersStore } from '../../stores/usersStore';
import { getWorklogsByItem, Worklog } from '../../api/worklog.api';
import { formatHoursMinutes } from '../../utils/timesheet-capacity';

interface ItemWorklogsDialogProps {
  mondayItemId: number;
  itemName: string;
  onClose: () => void;
}

const ItemWorklogsDialog: React.FC<ItemWorklogsDialogProps> = ({
  mondayItemId,
  itemName,
  onClose,
}) => {
  const { users } = useUsersStore();
  const dialogRef = useRef<HTMLDivElement>(null);

  const [itemWorklogs, setItemWorklogs] = useState<Worklog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch ALL worklogs for this item from the API (not limited by visible date range)
  useEffect(() => {
    let cancelled = false;

    async function fetchWorklogs() {
      setLoading(true);
      setError(null);
      try {
        const worklogs = await getWorklogsByItem(String(mondayItemId));
        if (!cancelled) {
          // Sort newest first
          const sorted = worklogs.sort((a, b) => b.date.localeCompare(a.date));
          setItemWorklogs(sorted);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load worklogs. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchWorklogs();

    return () => {
      cancelled = true;
    };
  }, [mondayItemId]);

  // Build user name map
  const userNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of users) {
      map.set(String(u.id), u.name);
    }
    return map;
  }, [users]);

  // Total logged
  const totalLogged = useMemo(
    () => itemWorklogs.reduce((sum, wl) => sum + wl.hours, 0),
    [itemWorklogs],
  );

  // Close on Escape
  useEffect(() => {
    const handle = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [onClose]);

  return (
    <>
    <div className="rp-item-worklogs-backdrop" onClick={onClose} />
    <div
      ref={dialogRef}
      className="rp-item-worklogs"
    >
      <div className="rp-item-worklogs__header">
        <span className="rp-item-worklogs__title">Worklogs</span>
        <button className="rp-item-worklogs__close" onClick={onClose}>&times;</button>
      </div>

      <div className="rp-item-worklogs__item-name">{itemName}</div>

      {loading ? (
        <div className="rp-item-worklogs__empty">Loading worklogs...</div>
      ) : error ? (
        <div className="rp-item-worklogs__empty">{error}</div>
      ) : itemWorklogs.length === 0 ? (
        <div className="rp-item-worklogs__empty">No worklogs logged for this item</div>
      ) : (
        <>
          <div className="rp-item-worklogs__summary">
            {itemWorklogs.length} worklog{itemWorklogs.length !== 1 ? 's' : ''} &middot; {formatHoursMinutes(totalLogged)} total
          </div>

          <div className="rp-item-worklogs__list">
            {itemWorklogs.map((wl) => {
              const userName = userNameMap.get(wl.mondayUserId) ?? `User ${wl.mondayUserId}`;

              return (
                <div key={wl.id} className="rp-item-worklogs__row">
                  <div className="rp-item-worklogs__row-main">
                    <span className="rp-item-worklogs__hours">{formatHoursMinutes(wl.hours)}</span>
                    <span className="rp-item-worklogs__date">{wl.date}</span>
                    <span className="rp-item-worklogs__user">{userName}</span>
                  </div>
                  {wl.description && (
                    <div className="rp-item-worklogs__desc">{wl.description}</div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
    </>
  );
};

export default ItemWorklogsDialog;
